/**
 * DailyBrief
 *
 * Main container component for the Daily Brief feature. Provides coaches with a snapshot
 * of athlete activity including workout completion tracking across 4 weeks, days remaining
 * on subscriptions, and questionnaire alerts from yesterday and today.
 *
 * Features:
 * - Time-based greeting (Good Morning/Afternoon/Evening)
 * - Filter athletes by group
 * - Sort by name, completion percentage, days left, or alert count
 * - Infinite scroll pagination
 * - Auto-saves user preferences (filters, sorting)
 * - Activity tracking integration
 * - Intercom support for admin users
 *
 * @component
 */
import React, { useEffect } from 'react';
import styled from '@emotion/styled';
import Helmet from 'react-helmet';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import Layout from '../../shared/components/Layout/Layout';
import DailyBriefTable from './components/DailyBriefTable';
import DailyBriefFilter from './components/DailyBriefFilter';
import {
  fetchDailyBrief,
  fetchSettings,
  resetDailyBriefData,
} from './ducks/dailyBriefActions';

const PageWrapper = styled('div')`
  display: flex;
  flex-direction: column;
  height: 100%;
  padding-top: 49px;
  background: #fcfcfc;
  width: 100%;
`;

const HeaderSection = styled('div')`
  background: #ffffff;
  padding: 16px;
  border-bottom: 1px solid #eeeeee;
  box-shadow: 0px 16px 24px rgba(48, 49, 51, 0.1),
    0px 0px 1px rgba(48, 49, 51, 0.05);
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const HeaderContent = styled('div')`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const GreetingSection = styled('div')`
  display: flex;
  flex-direction: column;
  gap: 0;
`;

const Greeting = styled('h1')`
  font-family: 'Nunito Sans', sans-serif;
  font-size: 19px;
  font-weight: 700;
  color: #444444;
  margin: 0;
  line-height: 1.5;
`;

const DateText = styled('p')`
  font-family: 'Nunito Sans', sans-serif;
  font-size: 16px;
  font-weight: 400;
  color: #9e9e9e;
  margin: 0;
  line-height: 1.5;
  order: -1;
`;

const FilterSection = styled('div')`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
`;

const ContentWrapper = styled('div')`
  flex: 1;
  overflow: auto;
  width: 100%;
  box-sizing: border-box;
`;

const getGreeting = () => {
  const hour = new Date().getHours();
  if (hour < 12) return 'Good Morning';
  if (hour < 17) return 'Good Afternoon';
  return 'Good Evening';
};

const formatDate = () => {
  const today = new Date();
  const options = {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  };
  const dateStr = today.toLocaleDateString('en-US', options);

  // Add ordinal suffix to day
  const day = today.getDate();
  const suffix = ['th', 'st', 'nd', 'rd'][
    day % 10 > 3 || Math.floor((day % 100) / 10) === 1 ? 0 : day % 10
  ];

  return dateStr.replace(/\d+/, `${day}${suffix}`);
};

const DailyBrief = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const currentUser = useSelector((state) => state.auth.data.currentUser);
  const accountCode = currentUser?.accountCode;
  const isAthlete = currentUser?.admin === 0;

  const {
    data,
    settings,
    isLoading,
    isLoadingMore,
    error,
    settingsLoaded,
    lastFetch,
    hasMoreData,
    hasAssignments,
    hasQuestionnaireAlerts,
  } = useSelector((state) => state.dailyBrief.data);

  const {
    filterType,
    filterIds,
    sortBy,
    sortDirection,
    currentPage,
    pageSize,
  } = useSelector((state) => state.dailyBrief.ui);

  // Redirect athletes to workouts - Daily Brief is coach-only
  useEffect(() => {
    if (isAthlete) {
      history.replace('/workouts');
    }
  }, [isAthlete, history]);

  const userName = currentUser?.firstName
    ? `Coach ${currentUser.firstName}`
    : 'Coach';

  useEffect(() => {
    if (accountCode && !settingsLoaded) {
      dispatch(fetchSettings(accountCode));
    }
  }, [accountCode, settingsLoaded, dispatch]);

  useEffect(() => {
    if (settingsLoaded) {
      dispatch(resetDailyBriefData());
    }
  }, [filterType, filterIds, sortBy, sortDirection, settingsLoaded, dispatch]);

  useEffect(() => {
    const hasNoFilterType = !filterType;
    const hasGroupSelection = filterType === 2 && filterIds !== null;

    if (
      accountCode
      && settingsLoaded
      && (hasNoFilterType || hasGroupSelection)
    ) {
      dispatch(
        fetchDailyBrief(accountCode, {
          filterType,
          filterIds: filterIds !== null ? [filterIds] : [],
          sortBy,
          sortDir: sortDirection,
          page: currentPage,
          pageSize,
        }),
      );
    }
  }, [
    accountCode,
    settingsLoaded,
    filterIds,
    sortBy,
    sortDirection,
    currentPage,
    pageSize,
    dispatch,
    filterType,
  ]);

  const hasLoadedOnce = Boolean(lastFetch);

  useEffect(() => {
    const isAdmin = currentUser?.admin;
    const userId = currentUser?.id;

    if (isAdmin && userId) {
      window.intercomSettings = {
        app_id: 'lryqyabv',
        user_id: userId.toString(),
      };

      const w = window;
      const ic = w.Intercom;
      if (typeof ic === 'function') {
        ic('reattach_activator');
        ic('update', window.intercomSettings);
      } else {
        const d = document;
        const i = function intercomPlaceholder(...args) {
          i.c(args);
        };
        i.q = [];
        i.c = function intercomCall(args) {
          i.q.push(args);
        };
        w.Intercom = i;
        const l = function loadIntercom() {
          const s = d.createElement('script');
          s.type = 'text/javascript';
          s.async = true;
          s.src = 'https://widget.intercom.io/widget/lryqyabv';
          const x = d.getElementsByTagName('script')[0];
          x.parentNode.insertBefore(s, x);
        };
        if (document.readyState === 'complete') {
          l();
        } else if (w.attachEvent) {
          w.attachEvent('onload', l);
        } else {
          w.addEventListener('load', l, false);
        }
      }
    }

    return () => {
      if (window.Intercom) {
        window.Intercom('shutdown');
      }
    };
  }, [currentUser?.admin, currentUser?.id]);

  // Don't render anything for athletes
  if (isAthlete) {
    return null;
  }

  const showEmptyState = hasLoadedOnce && !isLoading && (!data || data.length === 0);
  const hasFilterApplied = filterType !== null && filterIds !== null;

  const showHeader = !showEmptyState || (showEmptyState && hasFilterApplied);
  return (
    <Layout fullWidth>
      <Helmet>
        <title>Daily Brief - TeamBuildr</title>
      </Helmet>
      <PageWrapper>
        {showHeader && (
          <HeaderSection>
            <HeaderContent>
              <GreetingSection>
                <DateText>{formatDate()}</DateText>
                <Greeting>
                  {getGreeting()}
                  {', '}
                  {userName}
                </Greeting>
              </GreetingSection>
              <FilterSection>
                <DailyBriefFilter accountCode={accountCode} />
              </FilterSection>
            </HeaderContent>
          </HeaderSection>
        )}
        <ContentWrapper>
          <DailyBriefTable
            data={data}
            settings={settings}
            isLoading={isLoading}
            isLoadingMore={isLoadingMore}
            hasMoreData={hasMoreData}
            hasAssignments={hasAssignments}
            hasQuestionnaireAlerts={hasQuestionnaireAlerts}
            error={error}
            filterType={filterType}
            sortBy={sortBy}
            sortDirection={sortDirection}
            hasLoadedOnce={hasLoadedOnce}
            accountCode={accountCode}
          />
        </ContentWrapper>
      </PageWrapper>
    </Layout>
  );
};

export default DailyBrief;
